import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { Device, PairedRuuviTag, RuuviTag } from '../../types'
import {
  getPairedRuuviTag,
  getPairedRuuviTags,
  invokeAction,
  updateDevice
} from '../../services/deviceService'
import {
  Alert,
  AlertColor,
  AlertTitle,
  Box,
  Button,
  Stack,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'
import { BluetoothSearching, InfoOutlined } from '@mui/icons-material'
import RuuviTagQrCodeButton from './RuuviTagQrCodeButton'
import LoadingState from '../LoadingState'
import RuuviTagMacAddressInput from './RuuviTagMacAddressInput'
import RuuviTagScannerList from './RuuviTagScannerList'
import ValveInstallRuuviTagInstructions from '../ValveInstallRuuviTagInstructions'
import { RuuviTagType } from '../../enums'
import {
  createOrUpdateRuuviTag,
  detachRuuviTag,
  getRuuviTags,
  getRuuviTag,
  getRuuviTagAccelerationNow,
  getRuuviTagConditionNow
} from '../../services/ruuviTagService'
import { useDispatch } from 'react-redux'
import {
  createErrorNotification,
  createInfoNotification,
  createSuccessNotification,
  createWarningNotification
} from '../../redux/slices/notifications'
import { LoadingButton } from '@mui/lab'
import RuuviTagInstallationInfoAlert from '../RuuviTagInstallationInfoAlert'
import { usePolicy } from '../../hooks'
import { isSpine, isValve } from '../../utils/device'
import { formatMacAddress } from '../../utils/string'

const MAC_ADDRESS_LENGTH = 12

export interface RuuviTagScannerProps {
  device: Device
  onChange: (ruuviTag: RuuviTag | null) => void
  onCancel: () => void
  onFound?: (ruuviTag: RuuviTag) => void
  afterLoad?: (ruuviTag: RuuviTag) => void
  afterCreate?: (ruuviTag: RuuviTag) => void
  isDiscoveryEnabled?: boolean
  tagType?: RuuviTagType | null
  onTagTypeChange?: (type: RuuviTagType) => void
  allowTagTypeSelection?: boolean
}

function RuuviTagScanner({
  device,
  onFound,
  onChange,
  onCancel,
  afterLoad,
  afterCreate,
  isDiscoveryEnabled = true,
  tagType,
  onTagTypeChange,
  allowTagTypeSelection: allowTagTypeSelectionProp
}: RuuviTagScannerProps) {
  const { t } = useTranslation('installation')

  const dispatch = useDispatch()
  const { canCreateRuuviTag } = usePolicy()
  const userCanCreateRuuviTag = canCreateRuuviTag()
  const discoveryEnabled = isDiscoveryEnabled
  const allowTagTypeSelection = allowTagTypeSelectionProp ?? !isValve(device)
  const defaultTagType = RuuviTagType.ATTACHED
  const toSelectableTagType = (value?: RuuviTagType | null) =>
    value === RuuviTagType.ATTACHED || value === RuuviTagType.DEVICE ? value : undefined
  const initialTagType = allowTagTypeSelection
    ? (toSelectableTagType(tagType) ?? defaultTagType)
    : (tagType ?? defaultTagType)
  const [internalTagType, setInternalTagType] = useState<RuuviTagType>(initialTagType)

  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (tagType === undefined) {
      return
    }

    const normalized = allowTagTypeSelection
      ? (toSelectableTagType(tagType) ?? defaultTagType)
      : (tagType ?? defaultTagType)

    if (normalized !== internalTagType) {
      setInternalTagType(normalized)
    }
  }, [tagType, allowTagTypeSelection, internalTagType])

  const effectiveTagType = allowTagTypeSelection
    ? (toSelectableTagType(tagType) ?? internalTagType)
    : (tagType ?? internalTagType)

  function updateTagType(nextType: RuuviTagType) {
    const normalized = allowTagTypeSelection
      ? (toSelectableTagType(nextType) ?? defaultTagType)
      : nextType

    setInternalTagType(normalized)
    onTagTypeChange?.(normalized)
  }

  function clearAssignmentBlock() {
    lastBlockedProjectRef.current = null
    setAssignmentBlockedMessage(null)
  }

  function getProjectDisplayLabel(tag: RuuviTag | PairedRuuviTag): string {
    const projectName = tag.asset?.project?.name?.trim()
    if (projectName) {
      return projectName
    }

    const projectId = tag.asset?.projectId
    if (typeof projectId === 'number') {
      return `#${projectId}`
    }

    return t('unknown', { defaultValue: 'Unknown' })
  }

  function isAssignmentBlocked(tag: RuuviTag | PairedRuuviTag): boolean {
    const assignedProjectId = tag.asset?.projectId ?? null

    if (assignedProjectId == null) {
      clearAssignmentBlock()
      return false
    }

    const projectLabel = getProjectDisplayLabel(tag)
    const message = t('ruuviTagMustBeDetachedBeforeReinstall', {
      project: projectLabel
    })

    setAssignmentBlockedMessage(message)

    const dedupeKey = `${assignedProjectId}:${projectLabel}`

    if (lastBlockedProjectRef.current !== dedupeKey) {
      dispatch(createWarningNotification(message))
      lastBlockedProjectRef.current = dedupeKey
    }

    return true
  }

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const macSourceRef = useRef<'scan' | 'manual' | null>(null)
  const isMountedRef = useRef(true)

  const [isScanning, setScanning] = useState<boolean>(false)

  const [isScanCompleted, setScanCompleted] = useState<boolean>(false)

  const [isLoading, setLoading] = useState<boolean>(false)

  const [foundRuuviTags, setFoundRuuviTags] = useState<PairedRuuviTag[]>([])

  const [macAddress, setMacAddress] = useState<string>('')
  const [notFoundAlertSeverity, setNotFoundAlertSeverity] = useState<AlertColor>('error')
  const [isRefreshingOrientation, setRefreshingOrientation] = useState<boolean>(false)
  const [loadedRuuviTag, setLoadedRuuviTag] = useState<RuuviTag | null>(null)
  const [selectedTag, setSelectedTag] = useState<RuuviTag | PairedRuuviTag | null>(null)
  const [notFoundMessage, setNotFoundMessage] = useState<string | null>(null)
  const [pendingCreateMac, setPendingCreateMac] = useState<string | null>(null)
  const [isCreatingRuuviTag, setCreatingRuuviTag] = useState<boolean>(false)
  const [assignmentBlockedMessage, setAssignmentBlockedMessage] = useState<string | null>(null)
  const [attachedTagOverride, setAttachedTagOverride] = useState<RuuviTag | null | undefined>(
    undefined
  )
  const [isReplaceDialogOpen, setReplaceDialogOpen] = useState<boolean>(false)
  const [pendingReplacementTag, setPendingReplacementTag] = useState<PairedRuuviTag | null>(null)
  const [isReplacingTag, setReplacingTag] = useState<boolean>(false)
  const attachedTagPromiseRef = useRef<Promise<RuuviTag | null> | null>(null)
  const lastBlockedProjectRef = useRef<string | null>(null)
  // We rely on paired tag response to include latest readings; no extra condition fetch.
  // const [fetchingTemp] = useState<Record<string, boolean>>({})

  function applyLoadedTag(tag: RuuviTag) {
    if (isAssignmentBlocked(tag)) {
      setSelectedTag(null)
      setLoadedRuuviTag(null)
      onChange(null)
      return
    }

    clearAssignmentBlock()

    const attachableBackendType =
      tag.type === RuuviTagType.ATTACHED ||
      tag.type === RuuviTagType.VALVE_IN ||
      tag.type === RuuviTagType.VALVE_OUT

    const hasBackendHost = Boolean(tag.deviceName ?? tag.device?.name)
    const fallbackType = allowTagTypeSelection
      ? (effectiveTagType ?? defaultTagType)
      : defaultTagType

    const nextType = allowTagTypeSelection
      ? attachableBackendType || hasBackendHost
        ? (toSelectableTagType(tag.type) ?? RuuviTagType.ATTACHED)
        : fallbackType
      : (tag.type ?? fallbackType)

    const shouldDisplaySelectedDevice =
      allowTagTypeSelection &&
      nextType === RuuviTagType.ATTACHED &&
      !attachableBackendType &&
      !hasBackendHost &&
      Boolean(device?.name)

    const normalizedTag: RuuviTag = {
      ...tag,
      type: nextType,
      deviceName:
        nextType === RuuviTagType.ATTACHED
          ? (tag.deviceName ?? (shouldDisplaySelectedDevice ? device.name : null))
          : null,
      device:
        nextType === RuuviTagType.ATTACHED
          ? (tag.device ?? (shouldDisplaySelectedDevice ? device : null))
          : null
    }

    if (allowTagTypeSelection) {
      updateTagType(nextType)
    }

    setSelectedTag(normalizedTag)
    setNotFoundMessage(null)
    setLoadedRuuviTag(normalizedTag)
    afterLoad?.(normalizedTag)
    onChange(normalizedTag)
  }

  async function loadRuuviTag(mac: string, controller: AbortController) {
    try {
      setLoading(true)
      setLoadedRuuviTag(null)
      setNotFoundMessage(null)
      setNotFoundAlertSeverity('error')
      setPendingCreateMac(null)
      setCreatingRuuviTag(false)

      const tag = await getRuuviTag(mac, { signal: controller.signal })

      applyLoadedTag(tag)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.code === 'ERR_CANCELED') {
          return
        }

        if (error.response?.status === 404) {
          const source = macSourceRef.current

          if (userCanCreateRuuviTag && device?.name) {
            setPendingCreateMac(mac)
            setNotFoundAlertSeverity('info')
            setNotFoundMessage(t('ruuviTagCreatePromptMessage', { mac: mac.toUpperCase() }))
            return
          }

          if (source === 'scan' && !userCanCreateRuuviTag) {
            setNotFoundAlertSeverity('info')
            setNotFoundMessage(t('ruuviTagPowerOnReminder'))
            dispatch(createInfoNotification(t('ruuviTagPowerOnReminder')))
            return
          }

          setNotFoundAlertSeverity('error')
          setNotFoundMessage(t('ruuviTagWasNotFoundByMacAddress'))
          return
        }

        setNotFoundMessage(null)
        dispatch(createErrorNotification(t('somethingWentWrong')))
        return
      }

      setNotFoundMessage(null)
      dispatch(createErrorNotification(t('somethingWentWrong')))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (macAddress.length !== MAC_ADDRESS_LENGTH) {
      setLoadedRuuviTag(null)
      setNotFoundMessage(null)
      setLoading(false)
      return undefined
    }

    const controller = new AbortController()

    loadRuuviTag(macAddress, controller)

    return () => controller.abort()
  }, [macAddress])

  useEffect(() => {
    if (!selectedTag) {
      return
    }

    if (selectedTag.type === effectiveTagType) {
      return
    }

    const nextTag: RuuviTag = {
      ...selectedTag,
      type: effectiveTagType
    }

    if (allowTagTypeSelection) {
      if (effectiveTagType === RuuviTagType.ATTACHED) {
        nextTag.deviceName = selectedTag.deviceName ?? device.name
        nextTag.device = selectedTag.device ?? device
      } else {
        nextTag.deviceName = null
        nextTag.device = null
      }
    }

    setSelectedTag(nextTag)

    if (loadedRuuviTag && loadedRuuviTag.id === nextTag.id) {
      setLoadedRuuviTag(nextTag)
    }

    onChange(nextTag)
  }, [selectedTag, effectiveTagType, loadedRuuviTag, onChange, device])

  function handleTagUpdate(tag: PairedRuuviTag) {
    setFoundRuuviTags((prev) => {
      const index = prev.findIndex((existing) => existing.id === tag.id)

      if (index === -1) {
        return [...prev, tag]
      }
      const next = [...prev]
      next[index] = {
        ...next[index],
        ...tag,
        latest: tag.latest ?? next[index].latest
      }

      return next
    })

    setSelectedTag((previous) => {
      if (!previous || previous.id !== tag.id) {
        return previous
      }

      return {
        ...previous,
        ...tag,
        type: previous.type
      }
    })

    setLoadedRuuviTag((previous) => {
      if (!previous || previous.id !== tag.id) {
        return previous
      }

      return {
        ...previous,
        ...tag,
        type: previous.type
      }
    })
  }

  function handleTagTypeChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (!allowTagTypeSelection) {
      return
    }

    const nextType = event.target.value as RuuviTagType
    updateTagType(nextType)
  }

  async function handleTagSelect(tag: PairedRuuviTag) {
    if (isAssignmentBlocked(tag)) {
      setSelectedTag(null)
      setLoadedRuuviTag(null)
      onChange(null)
      return
    }

    clearAssignmentBlock()

    const nextType = resolveNextTagType(tag)
    let attachedTag = getExistingAttachedTag()

    if (isSpine(device) && nextType === RuuviTagType.ATTACHED) {
      const forceRefresh = attachedTagOverride === null
      attachedTag = await fetchAttachedTagForDevice(forceRefresh)
    }

    if (
      isSpine(device) &&
      nextType === RuuviTagType.ATTACHED &&
      attachedTag &&
      attachedTag.id !== tag.id
    ) {
      setPendingReplacementTag(tag)
      setReplaceDialogOpen(true)
      return
    }

    applyTagSelection(tag, nextType)
  }

  // When scanning flag turns on, start the scan loop; clean up on stop/unmount
  useEffect(() => {
    if (!isScanning) return

    if (!discoveryEnabled) {
      setScanning(false)
      return
    }

    const controller = new AbortController()
    abortControllerRef.current = controller
    setScanCompleted(false)
    scanRuuviTags(controller).catch(() => {})
    return () => {
      try {
        controller.abort()
      } catch {
        /* ignore fetch errors within polling loop */
      }
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [isScanning, discoveryEnabled])

  async function scanRuuviTags(controller?: AbortController) {
    if (!discoveryEnabled) {
      return
    }

    if (!isScanning) {
      setScanning(true)
    }

    setFoundRuuviTags([])

    // Invoke action for the device.
    await invokeAction(
      device.name,
      {
        ruuvi_pairing: 60
      },
      {
        signal: controller?.signal
      }
    )

    // Poll for the paired ruuvi tags every 2 seconds.
    const TWO_SECONDS = 2 * 1000
    const interval = setInterval(async () => {
      try {
        const tags = await getPairedRuuviTags(device.name, { signal: controller?.signal })
        if (Array.isArray(tags)) {
          setFoundRuuviTags((prev) => {
            if (tags.length === 0) return prev

            const prevMap = new Map(prev.map((tag) => [tag.id, tag]))

            const next = (tags as PairedRuuviTag[]).map((incoming) => {
              const existing = prevMap.get(incoming.id)

              if (!existing) {
                onFound?.(incoming)
                return incoming
              }

              const previousProjectId = existing.asset?.projectId ?? null
              const incomingProjectId = incoming.asset?.projectId ?? null

              if (previousProjectId === null && incomingProjectId !== null) {
                return {
                  ...existing,
                  latest: incoming.latest ?? existing.latest
                }
              }

              return {
                ...incoming,
                latest: incoming.latest ?? existing.latest
              }
            })

            return next
          })
        }
      } catch (_) {
        // Do nothing if failed.
      }
    }, TWO_SECONDS)
    intervalRef.current = interval

    // Clear the interval after one minute.
    const ONE_MINUTE = 60 * 1000
    const timeout = setTimeout(() => {
      setScanning(false)
      setScanCompleted(true)
      clearInterval(interval)
    }, ONE_MINUTE)
    timeoutRef.current = timeout
  }
  // No extra fetchConditionsForTag; sensor-backend should provide latest condition when pairing.

  function handleManualMacChange(value: string) {
    macSourceRef.current = 'manual'
    clearAssignmentBlock()
    setMacAddress(value)
  }

  function handleQrMacDetected(value: string) {
    macSourceRef.current = 'scan'
    clearAssignmentBlock()
    setMacAddress(value)
  }

  function startScan() {
    if (isScanning || !discoveryEnabled) return
    clearAssignmentBlock()
    setScanCompleted(false)
    setScanning(true)
  }

  function handleScanAgain() {
    if (!discoveryEnabled) return
    clearAssignmentBlock()
    startScan()
  }

  async function handleRefreshOrientation() {
    if (isRefreshingOrientation || isScanning || foundRuuviTags.length === 0) {
      return
    }

    setRefreshingOrientation(true)
    try {
      const updatedTags = await Promise.all(
        foundRuuviTags.map(async (tag) => {
          const [conditionResult, accelerationResult] = await Promise.allSettled([
            getRuuviTagConditionNow(tag.id),
            getRuuviTagAccelerationNow(tag.id)
          ])

          const updatedTag: PairedRuuviTag = {
            ...tag,
            latest: {
              ...tag.latest,
              condition:
                conditionResult.status === 'fulfilled'
                  ? conditionResult.value
                  : tag.latest?.condition,
              acceleration:
                accelerationResult.status === 'fulfilled'
                  ? accelerationResult.value
                  : tag.latest?.acceleration
            }
          }

          return updatedTag
        })
      )

      setFoundRuuviTags(updatedTags)
    } catch (_error) {
      dispatch(createErrorNotification(t('somethingWentWrong')))
    } finally {
      setRefreshingOrientation(false)
    }
  }

  function handleStartScan() {
    startScan()
  }

  function handleCancelScan() {
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort()
      } catch {
        /* ignore abort errors */
      }
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setScanning(false)
    setScanCompleted(false)
    clearAssignmentBlock()
  }

  async function handleConfirmCreate() {
    if (!pendingCreateMac) {
      return
    }

    const shouldAttachToDevice =
      !allowTagTypeSelection || effectiveTagType === RuuviTagType.ATTACHED

    if (shouldAttachToDevice && !device?.name) {
      return
    }

    setCreatingRuuviTag(true)
    try {
      const payloadDevice = shouldAttachToDevice ? (device?.name ?? null) : null
      const createdTag = await createOrUpdateRuuviTag({
        id: pendingCreateMac,
        device: payloadDevice,
        ...(allowTagTypeSelection ? { type: effectiveTagType } : {})
      })

      applyLoadedTag(createdTag)
      afterCreate?.(createdTag)
      dispatch(createSuccessNotification(t('createRuuviTagSuccess')))
      setNotFoundMessage(null)
      setPendingCreateMac(null)
      clearAssignmentBlock()
    } catch (creationError) {
      if (axios.isCancel(creationError)) {
        return
      }

      setNotFoundAlertSeverity('error')
      setNotFoundMessage(t('createRuuviTagError'))
      dispatch(createErrorNotification(t('createRuuviTagError')))
      setPendingCreateMac(null)
    } finally {
      setCreatingRuuviTag(false)
    }
  }

  function handleCancelCreate() {
    setPendingCreateMac(null)
    setCreatingRuuviTag(false)
    setNotFoundAlertSeverity('error')
    setNotFoundMessage(t('ruuviTagWasNotFoundByMacAddress'))
  }

  async function fetchAttachedTagForDevice(force = false): Promise<RuuviTag | null> {
    if (!isSpine(device) || !device?.name) {
      if (isMountedRef.current) {
        setAttachedTagOverride(null)
      }
      return null
    }

    const directAttached = device.attachedRuuviTag ?? null
    if (!force && directAttached) {
      return directAttached
    }

    const directAttachedId = device.attachedRuuviTagId ?? null
    if (!force && directAttachedId) {
      try {
        const tag = await getRuuviTag(directAttachedId)
        if (isMountedRef.current) {
          setAttachedTagOverride(tag)
        }
        return tag
      } catch (_error) {
        // Ignore and continue to other fetch strategies.
      }
    }

    if (!force && attachedTagOverride !== undefined) {
      return attachedTagOverride
    }

    if (!force && attachedTagPromiseRef.current) {
      return attachedTagPromiseRef.current
    }

    const fetchPromise = (async () => {
      try {
        let attached: RuuviTag | null = null

        try {
          const paired = await getPairedRuuviTag(device.name)
          if (
            paired &&
            paired.type === RuuviTagType.ATTACHED &&
            (paired.deviceName === device?.name || paired.device?.name === device?.name)
          ) {
            attached = paired
          }
        } catch (_error) {
          // Ignore; fallback to query by device name.
        }

        if (!attached) {
          const tags = await getRuuviTags({ deviceName: device.name })
          attached =
            tags.find(
              (tag) =>
                tag.type === RuuviTagType.ATTACHED &&
                (tag.deviceName === device?.name || tag.device?.name === device?.name)
            ) ?? null
        }

        if (isMountedRef.current) {
          setAttachedTagOverride(attached)
        }
        return attached
      } catch (_error) {
        dispatch(createErrorNotification(t('somethingWentWrong')))
        if (isMountedRef.current) {
          setAttachedTagOverride(null)
        }
        return null
      } finally {
        attachedTagPromiseRef.current = null
      }
    })()

    attachedTagPromiseRef.current = fetchPromise
    return fetchPromise
  }

  function getExistingAttachedTag(): RuuviTag | null {
    if (attachedTagOverride !== undefined) {
      return attachedTagOverride
    }

    if (device?.attachedRuuviTag) {
      return device.attachedRuuviTag
    }

    const tags = device?.ruuviTags ?? []
    return (
      tags.find(
        (tag) =>
          tag.type === RuuviTagType.ATTACHED &&
          (tag.deviceName === device?.name || tag.device?.name === device?.name)
      ) ?? null
    )
  }

  function resolveNextTagType(tag: PairedRuuviTag): RuuviTagType {
    const backendAttachable =
      tag.type === RuuviTagType.ATTACHED ||
      tag.type === RuuviTagType.VALVE_IN ||
      tag.type === RuuviTagType.VALVE_OUT

    if (allowTagTypeSelection) {
      if (backendAttachable) {
        return tag.type
      }
      if (effectiveTagType === RuuviTagType.ATTACHED || effectiveTagType === RuuviTagType.DEVICE) {
        return effectiveTagType
      }
      return defaultTagType
    }

    return tag.type ?? defaultTagType
  }

  function applyTagSelection(tag: PairedRuuviTag, nextType: RuuviTagType) {
    if (allowTagTypeSelection) {
      updateTagType(nextType)
    }

    const hasBackendHost = Boolean(tag.deviceName ?? tag.device?.name)
    const backendAttachable =
      tag.type === RuuviTagType.ATTACHED ||
      tag.type === RuuviTagType.VALVE_IN ||
      tag.type === RuuviTagType.VALVE_OUT
    const shouldDisplaySelectedDevice =
      allowTagTypeSelection &&
      nextType === RuuviTagType.ATTACHED &&
      !backendAttachable &&
      !hasBackendHost &&
      Boolean(device?.name)

    const normalizedTag: PairedRuuviTag = {
      ...tag,
      type: nextType,
      deviceName:
        nextType === RuuviTagType.ATTACHED
          ? (tag.deviceName ?? (shouldDisplaySelectedDevice ? device.name : null))
          : null,
      device:
        nextType === RuuviTagType.ATTACHED
          ? (tag.device ?? (shouldDisplaySelectedDevice ? device : null))
          : null
    }

    setSelectedTag(normalizedTag)
    setLoadedRuuviTag(null)
    setNotFoundMessage(null)
    setPendingCreateMac(null)
    onChange(normalizedTag)
  }

  async function handleConfirmReplaceAttachedTag() {
    const attachedTag = getExistingAttachedTag()
    const replacementTag = pendingReplacementTag

    if (!attachedTag || !replacementTag) {
      if (isMountedRef.current) {
        setReplaceDialogOpen(false)
        setPendingReplacementTag(null)
      }
      return
    }

    if (isMountedRef.current) {
      setReplacingTag(true)
    }
    try {
      await detachRuuviTag(attachedTag)
      if (device?.name) {
        await updateDevice(device.name, { attachedRuuviTag: null })
      }
      if (!isMountedRef.current) {
        return
      }
      setAttachedTagOverride(null)
      applyTagSelection(replacementTag, resolveNextTagType(replacementTag))
    } catch (_error) {
      dispatch(createErrorNotification(t('ruuvitag:detachRuuviTagError')))
    } finally {
      if (isMountedRef.current) {
        setReplacingTag(false)
        setReplaceDialogOpen(false)
        setPendingReplacementTag(null)
      }
    }
  }

  function handleCancelReplaceAttachedTag() {
    setReplaceDialogOpen(false)
    setPendingReplacementTag(null)
  }

  useEffect(() => {
    setAttachedTagOverride(undefined)
    attachedTagPromiseRef.current = null
  }, [device?.name, device?.ruuviTags])

  return (
    <>
      <Stack direction="column" gap={2}>
        {isValve(device) ? (
          <ValveInstallRuuviTagInstructions valve={device} />
        ) : (
          <RuuviTagInstallationInfoAlert />
        )}

        {assignmentBlockedMessage && (
          <Alert severity="warning">
            <AlertTitle sx={{ m: 0 }}>{assignmentBlockedMessage}</AlertTitle>
          </Alert>
        )}

        {allowTagTypeSelection && (
          <FormControl sx={{ mt: 1 }}>
            <FormLabel>{t('ruuviTagTypeSelectionLabel')}</FormLabel>
            <RadioGroup
              row
              value={
                effectiveTagType === RuuviTagType.DEVICE
                  ? RuuviTagType.DEVICE
                  : RuuviTagType.ATTACHED
              }
              onChange={handleTagTypeChange}
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              <FormControlLabel
                value={RuuviTagType.ATTACHED}
                control={<Radio />}
                label={
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <span>{t('ruuviTagTypeOptionAttached')}</span>
                    <Tooltip title={t('ruuviTagTypeAttachedTooltip')} placement="top">
                      <InfoOutlined fontSize="small" color="action" />
                    </Tooltip>
                  </Stack>
                }
              />
              <FormControlLabel
                value={RuuviTagType.DEVICE}
                control={<Radio />}
                label={t('ruuviTagTypeOptionStandalone')}
              />
            </RadioGroup>
          </FormControl>
        )}

        {!discoveryEnabled && (
          <Alert severity="info">
            <AlertTitle sx={{ m: 0 }}>
              {t('ruuviTagDiscoveryDisabledWhileDeviceOffline')}
            </AlertTitle>
          </Alert>
        )}

        {isScanning ? (
          <>
            <LoadingState
              title={
                <Stack direction="row" gap={1} alignItems="center">
                  <BluetoothSearching fontSize="small" />
                  <span>{t('searchingRuuviTags')}</span>
                </Stack>
              }
              action={
                <Button variant="outlined" color="inherit" onClick={handleCancelScan}>
                  {t('cancel')}
                </Button>
              }
            />

            {foundRuuviTags.length > 0 && (
              <RuuviTagScannerList
                ruuviTags={foundRuuviTags}
                onSelect={handleTagSelect}
                onExitInstallation={onCancel}
                onTagUpdate={handleTagUpdate}
              />
            )}
          </>
        ) : isScanCompleted ? (
          <>
            {foundRuuviTags.length === 0 ? (
              <Alert severity="warning">
                <AlertTitle sx={{ m: 0 }}>{t('ruuviTagsWereNotFound')}</AlertTitle>
                <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleScanAgain}
                    disabled={!discoveryEnabled}
                  >
                    {t('scanAgainRefreshOrientations')}
                  </Button>
                  <Button variant="outlined" color="inherit" size="small" onClick={onCancel}>
                    {t('cancel')}
                  </Button>
                </Stack>
              </Alert>
            ) : (
              <Alert severity="info">
                <AlertTitle sx={{ m: 0 }}>{t('scanCompleted')}</AlertTitle>
                <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleScanAgain}
                    disabled={!discoveryEnabled}
                  >
                    {t('scanAgainRefreshOrientations')}
                  </Button>
                  <LoadingButton
                    variant="outlined"
                    color="inherit"
                    size="small"
                    loading={isRefreshingOrientation}
                    disabled={isRefreshingOrientation}
                    onClick={handleRefreshOrientation}
                  >
                    {t('refreshOrientation')}
                  </LoadingButton>
                  <Button variant="outlined" color="inherit" size="small" onClick={onCancel}>
                    {t('cancel')}
                  </Button>
                </Stack>
              </Alert>
            )}

            {foundRuuviTags.length > 0 && (
              <RuuviTagScannerList
                ruuviTags={foundRuuviTags}
                onSelect={handleTagSelect}
                onExitInstallation={onCancel}
              />
            )}
          </>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 1 }}
                  onClick={handleStartScan}
                  disabled={!discoveryEnabled}
                >
                  {t('startScan')}
                </Button>
              </Grid>
            </Grid>

            {foundRuuviTags.length > 0 && (
              <>
                <RuuviTagScannerList
                  ruuviTags={foundRuuviTags}
                  onSelect={handleTagSelect}
                  onExitInstallation={onCancel}
                />
                <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleScanAgain}
                    disabled={!discoveryEnabled}
                  >
                    {t('scanAgainRefreshOrientations')}
                  </Button>
                  <LoadingButton
                    variant="outlined"
                    color="inherit"
                    size="small"
                    loading={isRefreshingOrientation}
                    onClick={handleRefreshOrientation}
                    disabled={isRefreshingOrientation}
                  >
                    {t('refreshOrientation')}
                  </LoadingButton>
                </Stack>
              </>
            )}
          </>
        )}

        {/* Manual MAC input and create prompt */}
        <Box sx={{ mt: 2 }}>
          <RuuviTagMacAddressInput
            disabled={isLoading}
            onChange={handleManualMacChange}
            value={macAddress}
          />

          <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
            <RuuviTagQrCodeButton disabled={isLoading} onMacDetected={handleQrMacDetected} />
          </Stack>

          {notFoundMessage && (
            <Alert severity={notFoundAlertSeverity} sx={{ mt: 2 }}>
              <AlertTitle sx={{ m: 0 }}>{notFoundMessage}</AlertTitle>
              {pendingCreateMac && (
                <Stack
                  direction="row"
                  gap={1}
                  sx={{ mt: 2 }}
                  justifyContent="flex-end"
                  flexWrap="wrap"
                >
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={handleCancelCreate}
                  >
                    {t('actions:cancel')}
                  </Button>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleConfirmCreate}
                    loading={isCreatingRuuviTag}
                  >
                    {t('ruuviTagCreatePromptConfirm')}
                  </LoadingButton>
                </Stack>
              )}
            </Alert>
          )}

          {loadedRuuviTag && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle sx={{ m: 0 }}>{t('ruuviTagWasFound')}</AlertTitle>
              {loadedRuuviTag.asset?.name ?? macAddress.toUpperCase()}
            </Alert>
          )}
        </Box>
      </Stack>

      <Dialog open={isReplaceDialogOpen} onClose={handleCancelReplaceAttachedTag} maxWidth="sm">
        <DialogTitle>{t('deviceAlreadyHasAttachedTagTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('deviceAlreadyHasAttachedTagDescription', {
              existingTag: formatMacAddress(getExistingAttachedTag()?.id ?? ''),
              newTag: formatMacAddress(pendingReplacementTag?.id ?? '')
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleCancelReplaceAttachedTag}>
            {t('keepExistingTag')}
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleConfirmReplaceAttachedTag}
            loading={isReplacingTag}
          >
            {t('replaceAttachedTag')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RuuviTagScanner
